import { useCallback } from "react";
import ChartCard from "../../components/ChartCard";
import PlaceholderCard from "../../components/PlaceholderCard";
import StatTile from "../../components/StatTile";
import VolumeChartCard from "../../components/VolumeChartCard";
import StackedColumns, { EmptyChartNote } from "../../components/charts/StackedColumns";
import { CardGrid, NoteCard, Section } from "../../components/Layout";
import { useDashboardData } from "../../hooks/useDashboardData";
import { fetchMessaging, fetchPlatformVolume } from "../../dashboardApi";
import {
  formatCents,
  formatDayTick,
  formatNumber,
  formatPercent,
  formatUtcDayKey,
} from "../../format";
import { mixpanelLink, TWILIO_USAGE } from "../../mixpanel";
import { DELIVERY_TONE } from "../../styles";
import {
  DateRangeKey,
  MessagingResponse,
  PlatformStatsResponse,
  PlatformVolumeResponse,
} from "../../types";
import TopSendersCard from "./TopSendersCard";

/**
 * Volume and spend for the page's date range are LIVE, straight from Twilio's
 * Usage Records — one call at the main account, which rolls up every
 * sub-account automatically. They are fetched here rather than with the rest of
 * the Home tab so a slow or failing Twilio degrades only these two cards.
 *
 * Volume HISTORY is cached, and that is a difference of clock rather than of
 * source — it still comes from Twilio, not from Markit's `dailyTwilioLogs`
 * mirror. A range total is one call (~0.6s); one row per day needs a
 * category-filtered call per series, because Markit's main account reports 522
 * usage categories and an unfiltered daily query returns one row per category
 * per day. Three filtered calls cover 2022-2026 in ~6.3s — too slow for a page
 * load, and it only changes once a day.
 *
 * Delivery rate is CACHED for a different reason entirely. Twilio's Usage
 * Records expose no delivered/undelivered/failed breakdown at all — that only
 * exists in `dailyTwilioLogs`, which carries 27k-106k rows a day, so it is read
 * once a night rather than on a page load. It also does NOT extend backwards:
 * the historical revenue backfill deliberately skips that scan, so days before
 * the rollup started have revenue but no delivery figures, and they are drawn as
 * gaps rather than as zeros.
 */
const DELIVERY_SERIES = DELIVERY_TONE.map(tone => ({
  key: tone.key,
  label: tone.label,
  color: tone.color,
}));

const ChannelSection = ({
  range,
  platform,
  platformError,
  dimmed,
}: {
  range: DateRangeKey;
  platform: PlatformStatsResponse | undefined;
  platformError: string | undefined;
  dimmed: boolean;
}) => {
  const fetcher = useCallback(() => fetchMessaging(range), [range]);
  const messaging = useDashboardData<MessagingResponse>(fetcher, [range]);

  // The volume history is deliberately NOT scoped to the page's date range: the
  // whole point of it is a multi-year view, and the backing document is one
  // Firestore read whatever slice you want. Its own granularity toggle picks
  // the window instead.
  const volumeFetcher = useCallback(() => fetchPlatformVolume(), []);
  const volume = useDashboardData<PlatformVolumeResponse>(volumeFetcher, []);

  const syncedAt = platform?.sync.lastRunFinishedAt ?? undefined;

  // A day whose `deliverability` is null was never scanned — a revenue-only
  // backfilled day. It is dropped from the chart and the rate rather than
  // counted as four zeros, which would read as a real 0% delivery outcome.
  const deliveryByDay = (platform?.days ?? [])
    .filter(day => day.deliverability !== null)
    .map(day => ({
      date: day.date,
      ...day.deliverability!,
    }));

  const unscannedDays = platform?.missingDeliverabilityDays ?? [];

  const totals = deliveryByDay.reduce(
    (sum, day) => ({
      delivered: sum.delivered + day.delivered,
      undelivered: sum.undelivered + day.undelivered,
      failed: sum.failed + day.failed,
      pending: sum.pending + day.pending,
    }),
    { delivered: 0, undelivered: 0, failed: 0, pending: 0 }
  );

  // In-flight messages are excluded from the denominator rather than counted as
  // failures — a message Twilio hasn't reported on yet is not a delivery
  // outcome, and including it would drag the rate down on the most recent day.
  const settled = totals.delivered + totals.undelivered + totals.failed;
  const deliveryRate = settled > 0 ? totals.delivered / settled : null;

  return (
    <Section
      title="Channel"
      description="Volume and spend are live from Twilio; the volume history is Twilio's own daily records, rebuilt nightly. Delivery rate comes from the nightly rollup of Markit's own message logs."
    >
      <CardGrid min={220}>
        <StatTile
          label="Messages sent"
          value={
            messaging.data ? formatNumber(messaging.data.messagesSent) : "—"
          }
          secondary={
            messaging.error
              ? "Twilio unavailable"
              : "Outbound SMS + MMS, all sub-accounts"
          }
          tone={messaging.error ? "warning" : "neutral"}
          freshness={{ kind: "live" }}
        />
        <StatTile
          label="Twilio spend"
          value={messaging.data ? formatCents(messaging.data.spendCents) : "—"}
          secondary={
            messaging.error
              ? "Twilio unavailable"
              : "Total billed for the range, all sub-accounts"
          }
          tone={messaging.error ? "warning" : "neutral"}
          freshness={{ kind: "live" }}
        />
        <StatTile
          label="Messages sent, all time"
          value={
            volume.data?.totals ? formatNumber(volume.data.totals.messagesSent) : "—"
          }
          secondary={
            volume.data?.windowFrom
              ? `Since ${formatUtcDayKey(volume.data.windowFrom)} · Twilio's own daily records`
              : "Not yet rebuilt by the nightly sync"
          }
          freshness={{ kind: "cached", updatedAt: volume.data?.computedAt ?? undefined }}
        />
        <StatTile
          label="Delivery rate"
          value={formatPercent(deliveryRate)}
          secondary={`${formatNumber(totals.delivered)} delivered of ${formatNumber(
            settled
          )} settled`}
          tone={
            deliveryRate === null
              ? "neutral"
              : deliveryRate >= 0.95
              ? "good"
              : deliveryRate >= 0.9
              ? "warning"
              : "critical"
          }
          freshness={{ kind: "cached", updatedAt: syncedAt }}
        />
      </CardGrid>

      <div style={{ marginTop: 16 }}>
        <VolumeChartCard
          title="Message volume, platform-wide"
          subtitle="Twilio Usage Records at the main account · rolls up every sub-account"
          days={volume.data?.days ?? []}
          error={volume.error}
          loading={volume.loading}
          dimmed={volume.refreshing}
          emptyLabel="The nightly sync hasn't rebuilt the volume history yet."
          freshness={{ kind: "cached", updatedAt: volume.data?.computedAt ?? undefined }}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <CardGrid min={340}>
          <TopSendersCard />

          <ChartCard
            title="Delivery outcomes, by day"
            subtitle="Nightly rollup · Firestore dailyTwilioLogs, bucketed by Twilio status"
            series={DELIVERY_SERIES}
            dimmed={dimmed}
            freshness={{ kind: "cached", updatedAt: syncedAt }}
            table={{
              categoryLabel: "Day",
              rows: deliveryByDay.map(day => ({
                category: day.date,
                values: {
                  delivered: day.delivered,
                  undelivered: day.undelivered,
                  failed: day.failed,
                  pending: day.pending,
                },
              })),
            }}
          >
            {platformError ? (
              <EmptyChartNote label={platformError} />
            ) : deliveryByDay.length === 0 ? (
              <EmptyChartNote label="No delivery data for this range yet." />
            ) : (
              <StackedColumns
                data={deliveryByDay}
                series={DELIVERY_SERIES}
                categoryKey="date"
                tickFormatter={formatDayTick}
                labelFormatter={formatDayTick}
              />
            )}
          </ChartCard>
        </CardGrid>
      </div>

      <div style={{ marginTop: 16 }}>
        <CardGrid min={340}>
          <PlaceholderCard
            title="Texts sent (Mixpanel)"
            source="Mixpanel · KPIs · Texts Sent (insights 35370704)"
            shape="line"
            link={mixpanelLink.insights("35370704")}
            linkLabel="Open in Mixpanel"
          />
        </CardGrid>
      </div>

      {messaging.error && (
        <div style={{ marginTop: 16 }}>
          <NoteCard title="Twilio usage is unavailable right now">
            {messaging.error} The two live cards above show &mdash; rather than a
            stale number. Delivery rate is unaffected: it comes from the nightly
            rollup, not from Twilio.{" "}
            <a href={TWILIO_USAGE} target="_blank" rel="noreferrer">
              Open Twilio usage
            </a>
            .
          </NoteCard>
        </div>
      )}

      {unscannedDays.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <NoteCard title="Some days have revenue but no delivery figures">
            {unscannedDays.length} day
            {unscannedDays.length === 1 ? "" : "s"} in this range
            {unscannedDays.length === 1 ? " was" : " were"} filled in by the
            historical revenue backfill, which deliberately does not run the
            delivery scan &mdash; that scan costs 60-95k Firestore reads per day and
            historical deliverability isn&rsquo;t wanted. Revenue for those days is
            real; they are missing from the delivery chart above rather than drawn
            as 0% delivered. Oldest: {unscannedDays[0]}.
          </NoteCard>
        </div>
      )}

      {platform && platform.missingDays.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <NoteCard title="Some days have no rollup yet">
            {platform.missingDays.length} day
            {platform.missingDays.length === 1 ? "" : "s"} in this range
            {platform.missingDays.length === 1 ? " has" : " have"} not been
            processed by the nightly sync, so
            {platform.missingDays.length === 1 ? " it is" : " they are"} missing
            from the charts above rather than being drawn as zero. Most recent
            gap: {platform.missingDays[platform.missingDays.length - 1]}.
          </NoteCard>
        </div>
      )}
    </Section>
  );
};

export default ChannelSection;
